/*
 * Sable — ready-to-dispatch markers (tag driven).
 *
 * A product is marked by tagging it, e.g.:
 *   ready:XS            -> the XS option value is ready to dispatch
 *   ready:M
 *   ready:XS:With Sleeves  -> only that combination is ready
 *
 * Each segment after "ready:" is matched against the product's real option
 * VALUES, so nothing is hardcoded to a specific option name — a tag works for
 * Size, Sleeves, Select Item or anything else the product happens to use.
 *
 * An option value gets a marker when any tag names it — loose on purpose, so a
 * shopper can see XS is ready before choosing sleeves. The callout under the
 * picker is strict: it appears only once the whole selection satisfies a tag.
 * Tags for combinations that are sold out (or don't exist) are ignored, so the
 * badge can't outlive the stock.
 *
 * Dawn replaces <variant-selects> on every change, so a MutationObserver
 * re-applies the markers — same approach as sable-conditional-variants.js.
 */
(function () {
  if (window.SableReadyToDispatch) return;
  window.SableReadyToDispatch = true;

  var READY_CLASS = 'sable-rtd-ready';
  var SR_CLASS = 'sable-rtd-sr';

  function slice(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function norm(value) {
    return (value == null ? '' : String(value)).trim().toLowerCase();
  }

  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function fieldsetsOf(vs) {
    return slice(vs.querySelectorAll('.product-form__input'));
  }

  function controlsOf(fs) {
    var radios = slice(fs.querySelectorAll('input[type="radio"]'));
    if (radios.length) return { type: 'radio', radios: radios, select: null };
    var select = fs.querySelector('select');
    return { type: select ? 'select' : 'none', radios: [], select: select };
  }

  function currentValue(fs) {
    var c = controlsOf(fs);
    if (c.type === 'radio') {
      for (var i = 0; i < c.radios.length; i++) {
        if (c.radios[i].checked) return c.radios[i].value;
      }
      return null;
    }
    if (c.type === 'select') return c.select.value;
    return null;
  }

  function valuesOf(fs) {
    var c = controlsOf(fs);
    if (c.type === 'radio') return c.radios.map(function (r) { return r.value; });
    if (c.type === 'select') return slice(c.select.options).map(function (o) { return o.value; });
    return [];
  }

  // "ready:xs:with sleeves" -> { <optionIndex>: <value>, ... }, keyed by the
  // option group each segment actually belongs to. Unknown segments are dropped.
  function parseEntries(tags, groups) {
    var index = groups.map(function (fs) {
      var map = {};
      valuesOf(fs).forEach(function (v) { map[norm(v)] = norm(v); });
      return map;
    });

    var entries = [];
    tags.forEach(function (tag) {
      var parts = String(tag).split(':');
      if (norm(parts[0]) !== 'ready') return;
      var entry = {};
      var found = 0;
      for (var p = 1; p < parts.length; p++) {
        var value = norm(parts[p]);
        if (!value) continue;
        for (var g = 0; g < index.length; g++) {
          if (index[g][value] != null) {
            entry[g] = value;
            found++;
            break;
          }
        }
      }
      if (found) entries.push(entry);
    });
    return entries;
  }

  function eachGroup(entry, fn) {
    for (var key in entry) {
      if (Object.prototype.hasOwnProperty.call(entry, key)) {
        if (fn(Number(key), entry[key]) === false) return false;
      }
    }
    return true;
  }

  // Ignore a tag unless a purchasable variant actually matches it.
  function entryAvailable(variants, entry) {
    if (!variants || !variants.length) return true;
    for (var k = 0; k < variants.length; k++) {
      var v = variants[k];
      if (!v.available) continue;
      var ok = eachGroup(entry, function (i, value) {
        return norm(v.options[i]) === value;
      });
      if (ok) return true;
    }
    return false;
  }

  // A marker means "ready in this value, in at least one combination" —
  // deliberately loose so a shopper can discover XS is ready before picking
  // sleeves. The callout applies the strict, whole-selection check.
  function marksValue(entry, i, value) {
    return entry[i] != null && entry[i] === value;
  }

  function satisfiedBySelection(entry, sel) {
    return eachGroup(entry, function (g, want) {
      return norm(sel[g]) === want;
    });
  }

  function markLabel(label, on, badgeText) {
    label.classList.toggle(READY_CLASS, !!on);
    var sr = label.querySelector('.' + SR_CLASS);
    if (on) {
      if (!sr) {
        sr = document.createElement('span');
        sr.className = 'visually-hidden ' + SR_CLASS;
        sr.textContent = ' (' + badgeText + ')';
        label.appendChild(sr);
      }
      label.setAttribute('title', badgeText);
    } else {
      if (sr) sr.parentNode.removeChild(sr);
      label.removeAttribute('title');
    }
  }

  function markOption(option, on) {
    if (option.getAttribute('data-sable-rtd-base') == null) {
      option.setAttribute('data-sable-rtd-base', option.textContent);
    }
    var base = option.getAttribute('data-sable-rtd-base');
    option.textContent = on ? base.replace(/\s+$/, '') + '  •' : base;
  }

  // The callout must point at a pill that is actually above it, so anchor to the
  // checked option in the LAST visible group. On a plain Style x Size product
  // that is the ready size itself; on one with a Sleeves row below Size it keeps
  // the caret attached to a real pill instead of the gap between two of them.
  function anchorLabel(groups) {
    for (var i = groups.length - 1; i >= 0; i--) {
      var fs = groups[i];
      if (fs.offsetParent === null) continue; // collapsed by the conditional script
      var c = controlsOf(fs);
      if (c.type !== 'radio') continue;
      for (var r = 0; r < c.radios.length; r++) {
        if (c.radios[r].checked) {
          return fs.querySelector('label[for="' + c.radios[r].id + '"]');
        }
      }
    }
    return null;
  }

  // Slide the callout under the selected pill and point its caret at it. The
  // bubble stays in normal flow, so it can never overlap the options below it —
  // it just shifts sideways, clamped to the picker's width.
  function positionBubble(note, label) {
    var bubble = note.querySelector('[data-sable-rtd-bubble]');
    if (!bubble) return;
    bubble.style.marginLeft = '0px';
    bubble.style.removeProperty('--sable-caret');
    if (!label) return;

    var noteBox = note.getBoundingClientRect();
    var bubbleBox = bubble.getBoundingClientRect();
    var labelBox = label.getBoundingClientRect();
    if (!noteBox.width || !bubbleBox.width) return;

    var centre = labelBox.left + labelBox.width / 2 - noteBox.left;
    var room = Math.max(0, noteBox.width - bubbleBox.width);
    var offset = Math.min(Math.max(centre - bubbleBox.width / 2, 0), room);
    var caret = Math.min(Math.max(centre - offset, 14), bubbleBox.width - 14);

    bubble.style.marginLeft = offset + 'px';
    bubble.style.setProperty('--sable-caret', caret + 'px');
  }

  function apply(pi) {
    var vs = pi.querySelector('variant-selects');
    if (!vs) return;

    var groups = fieldsetsOf(vs);
    if (!groups.length) return;

    var cfg = readJSON(vs, '[data-sable-rtd-config]') || {};
    var tags = readJSON(vs, '[data-sable-rtd]') || [];
    var variants = readJSON(vs, '[data-sable-variants]');
    var badgeText = cfg.badge || 'Ready to dispatch';

    var entries = parseEntries(tags, groups).filter(function (entry) {
      return entryAvailable(variants, entry);
    });
    var sel = groups.map(currentValue);

    groups.forEach(function (fs, i) {
      var c = controlsOf(fs);
      function ready(value) {
        var v = norm(value);
        return entries.some(function (entry) { return marksValue(entry, i, v); });
      }
      if (c.type === 'radio') {
        c.radios.forEach(function (radio) {
          var label = fs.querySelector('label[for="' + radio.id + '"]');
          if (label) markLabel(label, ready(radio.value), badgeText);
        });
      } else if (c.type === 'select') {
        slice(c.select.options).forEach(function (option) {
          markOption(option, ready(option.value));
        });
      }
    });

    var note = pi.querySelector('[data-sable-rtd-note]');
    if (!note) return;
    var badgeEl = note.querySelector('[data-sable-rtd-badge]');
    var legendEl = note.querySelector('[data-sable-rtd-legend]');
    var selected = entries.some(function (entry) { return satisfiedBySelection(entry, sel); });

    note.hidden = !entries.length;
    note.classList.toggle('sable-rtd--on', selected);
    if (badgeEl) badgeEl.textContent = badgeText;
    // The legend explains the markers; CSS hides it once a ready option is chosen.
    if (legendEl) legendEl.textContent = cfg.legend ? badgeText + ' ' + cfg.legend : badgeText;
    if (selected) positionBubble(note, anchorLabel(groups));
  }

  function initProductInfo(pi) {
    if (pi.dataset.sableRtd === '1') return;
    pi.dataset.sableRtd = '1';

    function run() {
      apply(pi);
    }

    // Run after Dawn's own change handling has settled.
    pi.addEventListener('change', function () {
      setTimeout(run, 0);
    });

    // Re-apply whenever Dawn swaps in a fresh <variant-selects>.
    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var a = 0; a < added.length; a++) {
          var n = added[a];
          if (n.nodeType !== 1) continue;
          var hit = (n.matches && n.matches('variant-selects')) || (n.querySelector && n.querySelector('variant-selects'));
          if (hit) {
            run();
            return;
          }
        }
      }
    });
    observer.observe(pi, { childList: true, subtree: true });

    // The callout is measured, so re-place it when the pills reflow.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(run, 120);
    });

    run();
  }

  function initAll() {
    slice(document.querySelectorAll('product-info')).forEach(initProductInfo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('product-info:loaded', function (e) {
    if (e.target && e.target.matches && e.target.matches('product-info')) initProductInfo(e.target);
  });
})();